import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  FileText,
  Link,
  CheckCircle,
  Circle,
  Loader2,
  AlertCircle,
  X,
  Plus,
  Server,
  Users,
  GitBranch,
} from 'lucide-react';
import { useIngest, useBulkImport } from '../hooks/useIngest';

const ACCEPTED_TYPES = {
  'text/markdown': ['.md'],
  'text/plain': ['.txt'],
  'application/json': ['.json'],
  'text/csv': ['.csv'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
};

const FILE_TYPE_ICONS = {
  md: FileText,
  txt: FileText,
  json: FileText,
  csv: FileText,
  pdf: FileText,
  docx: FileText,
  pptx: FileText,
};

function ProcessingStep({ step, status, current }) {
  const isComplete = status === 'complete';
  const isCurrent = status === 'current';
  const isPending = status === 'pending';

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${isCurrent ? 'bg-dark-700' : ''}`}>
      {isComplete && <CheckCircle size={20} className="text-green-400" />}
      {isCurrent && <Loader2 size={20} className="text-accent-purple animate-spin" />}
      {isPending && <Circle size={20} className="text-dark-500" />}
      <span className={`text-sm ${isComplete ? 'text-dark-300' : isCurrent ? 'text-white' : 'text-dark-500'}`}>
        {step}
      </span>
    </div>
  );
}

function ExtractedEntity({ entity, onRemove }) {
  const confidence = entity.confidence || 0;
  const confidenceColor = confidence >= 0.8 ? 'text-green-400' : confidence >= 0.5 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="flex items-center justify-between p-3 bg-dark-700 rounded-lg group">
      <div className="flex items-center gap-3">
        <Server size={16} className="text-blue-400" />
        <div>
          <p className="font-medium text-sm">{entity.name}</p>
          <p className="text-xs text-dark-400">{entity.type}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-xs font-mono ${confidenceColor}`}>
          {(confidence * 100).toFixed(0)}%
        </span>
        <button
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-dark-600 rounded transition-all"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function AddKnowledge({ stats }) {
  const [activeTab, setActiveTab] = useState('upload');
  const [files, setFiles] = useState([]);
  const [pasteContent, setPasteContent] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [processingSteps, setProcessingSteps] = useState([]);
  const [extractedEntities, setExtractedEntities] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef(null);
  const { extractFromText, loading: ingestLoading, lastResult } = useIngest();
  const { uploadFiles, processJob, status: bulkStatus, loading: bulkLoading } = useBulkImport();

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  }, []);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const simulateProcessing = useCallback(async () => {
    setIsProcessing(true);
    setExtractedEntities([]);

    const steps = [
      'Uploading content...',
      'Parsing document structure...',
      'Extracting entities with AI...',
      'Generating embeddings...',
      'Validating relationships...',
    ];

    setProcessingSteps(steps.map((step, i) => ({
      step,
      status: i === 0 ? 'current' : 'pending',
    })));

    for (let i = 0; i < steps.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setProcessingSteps(prev => prev.map((s, j) => ({
        ...s,
        status: j < i + 1 ? 'complete' : j === i + 1 ? 'current' : 'pending',
      })));
    }

    // Simulate extracted entities
    setExtractedEntities([
      { id: 1, name: 'new-service', type: 'SERVICE', confidence: 0.92, description: 'Extracted service' },
      { id: 2, name: 'Platform Team', type: 'TEAM', confidence: 0.85, description: 'Extracted team' },
      { id: 3, name: 'API Gateway', type: 'PATTERN', confidence: 0.78, description: 'Extracted pattern' },
    ]);

    setProcessingSteps(prev => prev.map(s => ({ ...s, status: 'complete' })));
    setIsProcessing(false);
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;
    await simulateProcessing();
  }, [files, simulateProcessing]);

  const handlePasteSubmit = useCallback(async () => {
    if (!pasteContent.trim()) return;
    await simulateProcessing();
  }, [pasteContent, simulateProcessing]);

  const handleUrlSubmit = useCallback(async () => {
    if (!urlInput.trim()) return;
    await simulateProcessing();
  }, [urlInput, simulateProcessing]);

  const handleConfirmAdd = useCallback(() => {
    // In real implementation, this would call the API to create entities
    alert(`Adding ${extractedEntities.length} entities to AOI`);
    setExtractedEntities([]);
    setFiles([]);
    setPasteContent('');
    setUrlInput('');
    setProcessingSteps([]);
  }, [extractedEntities]);

  const removeEntity = useCallback((id) => {
    setExtractedEntities(prev => prev.filter(e => e.id !== id));
  }, []);

  const tabs = [
    { id: 'upload', label: 'Upload File', icon: Upload },
    { id: 'paste', label: 'Paste Content', icon: FileText },
    { id: 'url', label: 'From URL', icon: Link },
  ];

  return (
    <div className="h-full flex">
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">Add Knowledge</h1>
            <p className="text-dark-300">
              Import documents and extract entities automatically using AI
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-accent-purple text-white'
                      : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div>
              <div
                onDrop={handleFileDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-dark-600 rounded-lg p-8 text-center cursor-pointer hover:border-accent-purple transition-colors"
              >
                <Upload size={40} className="mx-auto mb-4 text-dark-400" />
                <p className="text-lg mb-2">Drop files here or click to upload</p>
                <p className="text-sm text-dark-400">
                  Supports: MD, TXT, JSON, CSV, PDF, DOCX, PPTX
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".md,.txt,.json,.csv,.pdf,.docx,.pptx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText size={18} className="text-dark-400" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-dark-400">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-dark-600 rounded transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={handleUpload}
                    disabled={isProcessing}
                    className="w-full btn-primary mt-4 flex items-center justify-center gap-2"
                  >
                    {isProcessing ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Upload size={18} />
                    )}
                    Process {files.length} File{files.length > 1 ? 's' : ''}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Paste Tab */}
          {activeTab === 'paste' && (
            <div>
              <textarea
                value={pasteContent}
                onChange={(e) => setPasteContent(e.target.value)}
                placeholder="Paste your documentation, architecture notes, or any text content here..."
                className="w-full h-64 bg-dark-700 border border-dark-600 rounded-lg p-4 text-sm resize-none focus:outline-none focus:border-accent-purple transition-colors"
              />
              <button
                onClick={handlePasteSubmit}
                disabled={!pasteContent.trim() || isProcessing}
                className="w-full btn-primary mt-4 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <FileText size={18} />
                )}
                Extract Entities
              </button>
            </div>
          )}

          {/* URL Tab */}
          {activeTab === 'url' && (
            <div>
              <div className="flex gap-3">
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://confluence.ally.com/display/ARCH/..."
                  className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 focus:outline-none focus:border-accent-purple transition-colors"
                />
                <button
                  onClick={handleUrlSubmit}
                  disabled={!urlInput.trim() || isProcessing}
                  className="btn-primary px-6 flex items-center gap-2 disabled:opacity-50"
                >
                  {isProcessing ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Link size={18} />
                  )}
                  Fetch
                </button>
              </div>
              <p className="text-xs text-dark-400 mt-2">
                Supports Confluence, GitLab, and public web pages
              </p>
            </div>
          )}

          {/* Processing Steps */}
          {processingSteps.length > 0 && (
            <div className="mt-8 card p-4">
              <h3 className="text-sm font-semibold mb-3">Processing Pipeline</h3>
              <div className="space-y-1">
                {processingSteps.map((step, index) => (
                  <ProcessingStep key={index} {...step} />
                ))}
              </div>
            </div>
          )}

          {/* Extracted Entities */}
          {extractedEntities.length > 0 && (
            <div className="mt-8 card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">
                  Extracted Entities ({extractedEntities.length})
                </h3>
                <button
                  onClick={handleConfirmAdd}
                  className="btn-primary text-sm px-4 py-1 flex items-center gap-2"
                >
                  <Plus size={16} />
                  Confirm & Add
                </button>
              </div>
              <div className="space-y-2">
                {extractedEntities.map(entity => (
                  <ExtractedEntity
                    key={entity.id}
                    entity={entity}
                    onRemove={() => removeEntity(entity.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar Stats */}
      <div className="w-64 border-l border-dark-600 p-4 bg-dark-800">
        <h3 className="text-sm font-semibold text-dark-300 mb-4">Current Stats</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server size={16} className="text-blue-400" />
              <span className="text-sm">Services</span>
            </div>
            <span className="font-semibold">{stats?.service || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-green-400" />
              <span className="text-sm">Teams</span>
            </div>
            <span className="font-semibold">{stats?.team || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GitBranch size={16} className="text-purple-400" />
              <span className="text-sm">Patterns</span>
            </div>
            <span className="font-semibold">{stats?.pattern || 0}</span>
          </div>
        </div>

        <div className="mt-6 p-3 bg-dark-700 rounded-lg">
          <p className="text-xs text-dark-400">
            AI extraction uses Claude to identify entities and relationships from your content automatically.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AddKnowledge;
